import { svg, esc } from '../ui/icons.js';
import { dados, mesDeReferencia } from '../state.js';
import * as Documentos from '../data/documentos.js';
import { usuario } from '../auth.js';
import { abrirFicha } from './ficha.js';

/**
 * Recebimento de documentos, organizado pelo MÊS.
 *
 * A tela anterior era por empresa: um seletor escolhia uma, e aparecia a
 * matriz banco × mês dela. Mas a pergunta do dia 5 é "quem ainda não
 * mandou o extrato de agosto?", e respondê-la exigia abrir as 124
 * empresas uma por uma.
 *
 * Aqui o mês é escolhido uma vez e todas as empresas aparecem juntas, com
 * as contas como bolinhas na linha. Clicar na bolinha marca ali mesmo.
 *
 * Duas regras que vieram da gestão e continuam valendo:
 *
 *  1. Não existe "marcar tudo". Marcar conta por conta é proposital —
 *     obriga a pessoa a abrir o arquivo. Ela recusou o botão de lote.
 *  2. O PDF é obrigatório em TODA conta, esteja no Open Finance ou não. O
 *     Open é contexto, não dispensa nada.
 *
 * A visão por empresa não sumiu: virou o detalhe, na ficha, com os 12
 * meses. Clicar no nome da empresa leva até lá.
 */

const CICLO = ['faltante', 'analise', 'parcial', 'recebido'];

let aoMudarDados = () => {};
let statusPorBanco = new Map();
let regrasPorBanco = new Map();

export function configurar({ onChange }) {
  aoMudarDados = onChange || (() => {});

  const mes = document.getElementById('docMes');
  mes.value = mesDeReferencia().slice(0, 7);
  mes.addEventListener('change', render);

  ['docFiltroFalta', 'docFiltroResp', 'docFiltroBanco'].forEach((id) =>
    document.getElementById(id).addEventListener('change', desenhar)
  );
  document.getElementById('docBusca').addEventListener('input', desenhar);
}

const competenciaEscolhida = () => {
  const v = document.getElementById('docMes').value;
  return v ? `${v}-01` : mesDeReferencia();
};

/**
 * Todo mundo vê todas.
 *
 * Receber documento é função do setor, não da carteira: chega o extrato
 * de um cliente do Cleytson por e-mail, o Ademir baixa e salva na pasta.
 * Se a tela mostrasse só as dele, esse trabalho não teria onde ser
 * registrado. A RLS foi aberta junto — antes marcar exigia ser o
 * responsável.
 *
 * O filtro por responsável continua ali para quem quiser olhar só as
 * suas; a diferença é que agora é escolha, não imposição.
 */
function empresasDaTela() {
  return dados.empresas.filter(
    (e) => e.situacao !== 'encerrado' && !e.matriz_id && e.gera_fechamento !== false
  );
}

export async function render() {
  const [situacoes, regras] = await Promise.all([
    Documentos.statusDoMes(competenciaEscolhida()),
    Documentos.regrasDeTodas(),
  ]);
  statusPorBanco = new Map(situacoes.map((d) => [d.banco_id, d]));
  regrasPorBanco = regras;
  preencherFiltros();
  desenhar();
}

function preencherFiltros() {
  const selResp = document.getElementById('docFiltroResp');
  if (selResp && !selResp.options.length) {
    selResp.innerHTML = '<option value="">Todos os responsáveis</option>' +
      dados.colaboradores.map((c) => `<option value="${c.id}">${esc(c.nome)}</option>`).join('');
  }

  const selBanco = document.getElementById('docFiltroBanco');
  if (selBanco) {
    const atual = selBanco.value;
    const nomes = [...new Set(
      empresasDaTela().flatMap((e) => (e.bancos || []).map((b) => b.nome))
    )].sort((a, b) => a.localeCompare(b, 'pt'));
    selBanco.innerHTML = '<option value="">Todos os bancos</option>' +
      nomes.map((n) => `<option value="${esc(n)}">${esc(n)}</option>`).join('');
    if (nomes.includes(atual)) selBanco.value = atual;
  }
}

const statusDe = (banco) => statusPorBanco.get(banco.id)?.status || 'faltante';

function desenhar() {
  const filtroFalta = document.getElementById('docFiltroFalta').value;
  const filtroResp = document.getElementById('docFiltroResp').value;
  const filtroBanco = document.getElementById('docFiltroBanco').value;
  const busca = document.getElementById('docBusca').value.trim().toLowerCase();

  const linhas = empresasDaTela()
    .map((e) => {
      const contas = (e.bancos || []).filter((b) => !filtroBanco || b.nome === filtroBanco);
      const recebidas = contas.filter((b) => statusDe(b) === 'recebido').length;
      return { empresa: e, contas, recebidas, faltam: contas.length - recebidas };
    })
    .filter(({ empresa, contas, faltam }) => {
      if (busca && !empresa.nome.toLowerCase().includes(busca)) return false;
      if (filtroResp && !(empresa.responsaveis || []).some((r) => r.id === filtroResp)) return false;
      if (filtroBanco && !contas.length) return false;
      if (filtroFalta === 'falta') return faltam > 0;
      if (filtroFalta === 'completa') return contas.length > 0 && faltam === 0;
      return true;
    })
    .sort((a, b) => b.faltam - a.faltam || a.empresa.nome.localeCompare(b.empresa.nome, 'pt'));

  renderResumo(filtroBanco);
  renderLista(linhas);
}

function renderResumo(filtroBanco) {
  const todas = empresasDaTela().map((e) => {
    const contas = (e.bancos || []).filter((b) => !filtroBanco || b.nome === filtroBanco);
    const recebidas = contas.filter((b) => statusDe(b) === 'recebido').length;
    return { contas: contas.length, recebidas };
  });

  const comConta = todas.filter((x) => x.contas > 0);
  const completas = comConta.filter((x) => x.recebidas === x.contas).length;
  const parciais = comConta.filter((x) => x.recebidas > 0 && x.recebidas < x.contas).length;
  const zeradas = comConta.filter((x) => x.recebidas === 0).length;
  const semConta = todas.length - comConta.length;

  document.getElementById('docResumo').innerHTML = `
    <span class="doc-origem open">${completas} completas</span>
    <span class="doc-origem pdf">${parciais} parciais</span>
    <span class="doc-origem falta">${zeradas} sem nada</span>
    ${semConta ? `<span class="doc-origem neutro">${semConta} sem conta cadastrada</span>` : ''}
    <span class="doc-aviso">
      <b>O PDF é obrigatório em todas as contas</b>, esteja no Open ou não.
      Clique na bolinha para marcar — uma conta de cada vez, abrindo o arquivo.
    </span>`;
}

function renderLista(linhas) {
  const alvo = document.getElementById('docLista');

  if (!linhas.length) {
    alvo.innerHTML = `<p style="font-size:13px;color:var(--text-muted);margin:4px;">
      Nenhuma empresa com esses filtros. Se está em "Só quem falta", pode ser que
      não falte nada — o que é uma boa notícia.</p>`;
    return;
  }

  alvo.innerHTML = linhas.map(({ empresa: e, contas, recebidas, faltam }) => {
    const responsaveis = (e.responsaveis || []).map((r) => r.nome).join(', ') || '—';
    // Qualquer colaborador marca: a RLS foi aberta na etapa 22 porque
    // receber documento é tarefa do setor, não da carteira.
    const editavel = !!usuario();

    const marcador = !contas.length
      ? (e.sem_conta_bancaria
          ? '<span class="doc-origem neutro">Não tem conta bancária</span>'
          : '<span class="doc-origem falta">Conta não cadastrada</span>')
      : faltam === 0
        ? '<span class="doc-origem open">Completa</span>'
        : `<span class="doc-origem falta">Faltam ${faltam} de ${contas.length}</span>`;

    return `
      <div class="doc-linha">
        <div class="doc-linha-cab">
          <button type="button" class="doc-empresa" data-empresa="${e.id}">${esc(e.nome)}</button>
          <span class="doc-resp">${esc(responsaveis)}</span>
          ${marcador}
        </div>
        <div class="doc-contas">
          ${contas.map((b) => {
            const st = statusDe(b);
            const info = Documentos.STATUS_DOC[st];
            return `
              <button type="button" class="doc-chip ${st}" ${editavel ? '' : 'disabled'}
                      data-banco="${b.id}" data-empresa="${e.id}" data-status="${st}"
                      data-regra="${esc(regrasPorBanco.get(b.id)?.regra || '')}" data-severidade="${esc(regrasPorBanco.get(b.id)?.severidade || '')}"
                      title="${esc(b.nome)} — ${info.rotulo}${editavel ? ' (clique para trocar)' : ''}">
                <span class="doc-dot" style="background:${info.cor};"></span>
                <span class="doc-chip-nome">${esc(b.nome)}</span>
                ${b.agencia ? `<span class="doc-chip-conta">${esc(b.agencia)}·${esc(b.conta || '—')}</span>` : ''}
                ${b.open_finance ? '<span class="doc-chip-open">Open</span>' : ''}
              </button>`;
          }).join('')
          || `<button type="button" class="doc-chip vazia" data-cadastrar="${e.id}">
                ${svg('building', 13)} Cadastrar conta na ficha
              </button>`}
        </div>
      </div>`;
  }).join('');

  alvo.querySelectorAll('.doc-empresa').forEach((b) =>
    b.addEventListener('click', () => abrirFicha(b.dataset.empresa, 'documentacao'))
  );
  alvo.querySelectorAll('[data-cadastrar]').forEach((b) =>
    b.addEventListener('click', () => abrirFicha(b.dataset.cadastrar, 'open'))
  );
  alvo.querySelectorAll('.doc-chip[data-banco]:not([disabled])').forEach((chip) =>
    chip.addEventListener('click', () => trocarStatus(chip))
  );
}

async function trocarStatus(chip) {
  const atual = chip.dataset.status;
  const proximo = CICLO[(CICLO.indexOf(atual) + 1) % CICLO.length];
  const info = Documentos.STATUS_DOC[proximo];

  /*
   * Instituição com exigência própria — o Itaú só aceita o PDF
   * consolidado. A confirmação aparece na hora de marcar como recebido,
   * que é quando a pessoa está com o arquivo na frente e pode conferir.
   */
  let exigenciaCumprida = null;
  if (proximo === 'recebido' && chip.dataset.severidade === 'bloqueio') {
    if (!confirm(`${chip.dataset.regra}\n\nConfirma que o documento atende a essa exigência?`)) return;
    exigenciaCumprida = true;
  }

  // Pinta antes de salvar e desfaz se o banco recusar.
  const bolinha = chip.querySelector('.doc-dot');
  bolinha.style.background = info.cor;
  chip.className = `doc-chip ${proximo}`;
  chip.dataset.status = proximo;

  try {
    await Documentos.definirStatus({
      empresaId: chip.dataset.empresa,
      bancoId: chip.dataset.banco,
      competencia: competenciaEscolhida(),
      status: proximo,
      exigenciaCumprida,
    }, usuario().id);

    statusPorBanco.set(chip.dataset.banco, { banco_id: chip.dataset.banco, status: proximo });
    renderResumo(document.getElementById('docFiltroBanco').value);
    aoMudarDados();
  } catch (err) {
    const anterior = Documentos.STATUS_DOC[atual];
    bolinha.style.background = anterior.cor;
    chip.className = `doc-chip ${atual}`;
    chip.dataset.status = atual;
    alert('Não foi possível salvar: ' + err.message);
  }
}
